import {
    MutateOptions,
    MutationFunction,
    QueryClient,
    QueryKey,
    UseMutationOptions,
    useMutation,
    useQueryClient,
} from 'react-query'
import { QueryFilters } from 'react-query/types/core/utils'
import { Immutable } from 'immer'
import { DateTime } from 'luxon'
import { toast } from '../components/molecules/toast'
import { DEFAULT_BACKGROUND_QUERY_STALE_TIME, QUEUED_MUTATION_DEBOUNCE, TASK_REFETCH_INTERVAL } from '../constants'
import useQueryContext from '../context/QueryContext'
import { usePreviewMode, useToast } from '../hooks'
import { consumeReadOnlyWriteRejection, isReadOnlyError } from '../utils/readOnly'
import { getMonthsAroundDate } from '../utils/time'
import { TEvent } from '../utils/types'
import { emptyFunction, sleep } from '../utils/utils'

/**
 * Wrapper for useQueryClient that adds getImmutableQueryData method
 * getImmutableQueryData simply casts the result as Immutable so the editor disallows direct assignment
 */
export interface GTQueryClient extends QueryClient {
    getImmutableQueryData: <TData = unknown>(queryKey: QueryKey, filters?: QueryFilters) => Immutable<TData> | undefined
    getCurrentEvents: (
        date: DateTime,
        datetime_start: string,
        datetime_end: string
    ) => { events?: Immutable<TEvent[]>; blockStartTime: string }
}
/**
 * The start of the month block whose ['events', 'calendar', start] cache holds an event. An event that
 * crosses a month boundary belongs to the month it starts in.
 */
export const getEventBlockStartTime = (date: DateTime, datetime_start: string, datetime_end: string) => {
    const start = DateTime.fromISO(datetime_start)
    const end = DateTime.fromISO(datetime_end)
    const timeBlocks = getMonthsAroundDate(date, 1)
    const block =
        timeBlocks.find((block) => start >= block.start && end <= block.end) ??
        timeBlocks.find((block) => start >= block.start && start <= block.end)
    return (block?.start ?? start.startOf('month')).toISO()
}

export const useGTQueryClient = (): GTQueryClient => {
    const queryClient = useQueryClient() as GTQueryClient
    queryClient.getImmutableQueryData = <TData = unknown>(
        queryKey: QueryKey,
        filters?: QueryFilters
    ): Immutable<TData> | undefined => queryClient.getQueryData(queryKey, filters)

    queryClient.getCurrentEvents = (date: DateTime, datetime_start: string, datetime_end: string) => {
        const blockStartTime = getEventBlockStartTime(date, datetime_start, datetime_end)
        queryClient.cancelQueries(['events', 'calendar', blockStartTime])
        return {
            events: queryClient.getImmutableQueryData<TEvent[]>(['events', 'calendar', blockStartTime]),
            blockStartTime,
        }
    }

    return queryClient
}

/**
 * Provides a wrapper around react-query's useMutation that allows mutations to be queued
 * Optimistic updates will be applied immediately via the onMutation callback
 * Mutations will only be sent once all queued mutations have been completed
 * Queues are separated by the tag param - i.e. 'tasks' and 'overview' will get separate queues
 * Once a queue is cleared, the invalidateTagsOnSettled will be invalidated to trigger a refetch
 *
 * Mutations that modify an object MUST include an id field
 * and if the id is waiting to be assigned, then isOptimistic must be passed in as true
 **/
interface MutationOptions<TData, TError, TVariables, TContext>
    extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> {
    tag: QueryKey
    errorMessage: string
    invalidateTagsOnSettled?: QueryKey[]
}

export const useGTMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
    mutationFn: MutationFunction<TData, TVariables>,
    mutationOptions: MutationOptions<TData, TError, TVariables, TContext>,
    useQueueing = true
) => {
    const queryClient = useGTQueryClient()
    const oldToast = useToast()
    const { isPreviewMode } = usePreviewMode()
    const { getQueryQueue, getLastSentQuery, setLastSentQuery, getIdFromOptimisticId } = useQueryContext()

    const { mutate, ...rest } = useMutation(mutationFn, {
        ...mutationOptions,
        onMutate: useQueueing ? emptyFunction : mutationOptions.onMutate,
        onSuccess: (data, variables, context) => {
            const queue = getQueryQueue(mutationOptions.tag)
            const mutationContext = (useQueueing ? queue[0]?.context : context) as TContext | undefined
            mutationOptions.onSuccess?.(data, variables, mutationContext)
        },
        onSettled: async (data, error, variables, context) => {
            const queue = getQueryQueue(mutationOptions.tag)
            const thisRequest = queue.shift()
            const mutationContext = (useQueueing ? thisRequest?.context : context) as TContext | undefined
            mutationOptions.onSettled?.(data, error, variables, mutationContext)
            let sentNextRequest = false
            while (queue.length > 0 && !sentNextRequest) {
                const nextRequest = queue[0]
                if (nextRequest.optimisticId) {
                    const id = getIdFromOptimisticId(nextRequest.optimisticId)
                    if (!id) {
                        queue.shift()?.abort?.()
                        continue
                    }
                    nextRequest.send(id)
                    setLastSentQuery(mutationOptions.tag, nextRequest)
                    sentNextRequest = true
                } else {
                    nextRequest.send()
                    setLastSentQuery(mutationOptions.tag, nextRequest)
                    sentNextRequest = true
                }
                // Without this the last request of a burst never matches below, so the queue drains
                // without the refetch that reconciles the optimistic state with the server.
                setLastSentQuery(mutationOptions.tag, nextRequest)
            }
            if (!sentNextRequest) {
                await sleep(QUEUED_MUTATION_DEBOUNCE)
                // check if another request was sent during the debounce period
                if (getLastSentQuery(mutationOptions.tag) != thisRequest) return
                mutationOptions.invalidateTagsOnSettled?.forEach((tag) => queryClient.invalidateQueries(tag))
            }
        },
        onError: (error, variables, context) => {
            const queue = getQueryQueue(mutationOptions.tag)
            const mutationContext = (useQueueing ? queue[0]?.context : context) as TContext | undefined
            mutationOptions.onError?.(error, variables, mutationContext)
            // Read-only rejections already got their own toast. Most mutation fns rethrow a string,
            // so consume the one-shot marker recorded by the response interceptor.
            if (consumeReadOnlyWriteRejection() || isReadOnlyError(error)) return
            if (isPreviewMode) {
                toast(`Failed to ${mutationOptions.errorMessage}: Request failed.`, {
                    type: 'error',
                })
            } else {
                oldToast.show(
                    {
                        title: `Failed to ${mutationOptions.errorMessage}:`,
                        message: 'Request failed.',
                    },
                    {
                        autoClose: 4000,
                        pauseOnFocusLoss: false,
                        theme: 'light',
                    }
                )
            }
        },
    })
    const newMutate = (
        variables: TVariables,
        optimisticIdOrOptions?: string | MutateOptions<TData, TError, TVariables, TContext>,
        options?: MutateOptions<TData, TError, TVariables, TContext>
    ) => {
        let optimisticId = typeof optimisticIdOrOptions === 'string' ? optimisticIdOrOptions : undefined
        const mutateOptions = typeof optimisticIdOrOptions === 'string' ? options : optimisticIdOrOptions
        // When queueing, the request is held back, so the optimistic update has to
        // run here. When not queueing, react-query runs onMutate itself (see the
        // useMutation options above) — running it here too would apply it twice,
        // and would also skip it entirely for mutateAsync callers.
        const context = useQueueing ? mutationOptions.onMutate?.(variables) : undefined
        const queue = getQueryQueue(mutationOptions.tag)

        // if an optimistic ID is passed in, first check if it has already been resolved
        if (optimisticId) {
            const realId = getIdFromOptimisticId(optimisticId)
            if (realId) {
                optimisticId = undefined
                variables = { ...variables, id: realId }
            }
        }

        if (optimisticId) {
            if (queue.length === 0) {
                throw new Error(`Optimistic mutation queued with no pending requests with tag: ${mutationOptions.tag}`)
            } else {
                queue.push({
                    send: (id?: string) => mutate({ ...variables, id }, mutateOptions),
                    optimisticId,
                    context,
                    abort: () => {
                        mutationOptions.onError?.(
                            new Error('Could not find real id for optimistic id') as unknown as TError,
                            variables,
                            context as TContext
                        )
                    },
                })
            }
        } else {
            const queuedRequest = { send: () => mutate(variables, mutateOptions), context }
            queue.push(queuedRequest)
            if (queue.length === 1) {
                queuedRequest.send()
                setLastSentQuery(mutationOptions.tag, queuedRequest)
            }
        }
    }
    return {
        ...rest,
        mutate: newMutate,
    }
}

/**
 * If the window is focused, refetch regularly
 * If the window is not focused for more than refetchInterval, refetch when the user returns to the window
 **/
export const getBackgroundQueryOptions = (
    refetchInterval = TASK_REFETCH_INTERVAL,
    staleTime = DEFAULT_BACKGROUND_QUERY_STALE_TIME
) => ({
    staleTime,
    refetchInterval,
    refetchIntervalInBackground: false,
})
